import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DURATION, EASE_OUT_SOFT, EASE_SPRING } from "../lib/motion";
import { Lockup } from "./Lockup";
import { HexAmbient } from "./HexAmbient";
import { BgOrbs } from "./BgOrbs";
import { WelcomeCycle } from "./WelcomeCycle";

/**
 * Beyond Sure — AuthShell (v3.2, desktop split-screen).
 *
 * v3.2 fixes the desktop composition: instead of a phone layout stretched onto
 * a wide screen with empty green gutters + floating sticker pills, the shell
 * now becomes a REAL split-screen on ≥1024px:
 *
 *   LEFT panel  (~52% width)  full-bleed green brand world
 *     • Lockup top-left
 *     • Vercetti display headline + Ranade subline (editorial brand hero)
 *     • PhoneShowcase integrated as hero visual (auto-cycling, glass)
 *     • Single trust strip pinned near bottom
 *     • HexAmbient + slow gradient sheen drift behind it
 *
 *   RIGHT panel (~48% width)  crisp glass — not muddy gray
 *     • Hanken Grotesk Variable caps eyebrow ("SIGN IN · FREE 7-DAY TRIAL")
 *     • Vercetti display "Welcome." short heading
 *     • Form children (email field, T&C, Continue, OR, Google)
 *     • Footer microcopy
 *     • Form column hard-capped at max-w-[440px] so ultrawide doesn't sprawl
 *
 * Below 1024px the shell falls back to the single editorial column from v3.1
 * (the layout the user accepted on phone + tablet). Two changes from v3.1:
 *
 *   1. The two `xl:flex` floating sticker pills ("LIVE · COMPARING 6
 *      INSURERS", "IRDAI-LICENSED") that the user described as "dropped into
 *      the void" — DELETED entirely. No composition, never made the cut.
 *   2. The showcase slot now renders on tablet too (lg:hidden, not md:hidden)
 *      so tablet's "brand+showcase top, form below" stack works without an
 *      empty grey band where the showcase used to be hidden.
 *
 * Prop contract — unchanged except the slot rename:
 *   eyebrow / headline / subhead       per-screen brand copy
 *   showcase?                          interactive preview node (rendered on
 *                                       BOTH the desktop LEFT panel and the
 *                                       <lg editorial column)
 *   formTitle?                         short heading above the form on desktop
 *                                       RIGHT panel. Defaults to "Welcome.".
 *                                       OTP screen will pass "Enter your code."
 *   children                           the form / inputs / buttons
 *
 * The OTP screen next session will pass:
 *   eyebrow="Sign in · Free 7-day trial"
 *   headline="Insure smarter, live better." (LEFT brand stays constant)
 *   subhead="…" (same)
 *   formTitle="Enter your code."             (RIGHT swaps)
 *   showcase={<PhoneShowcase/>}              (LEFT still demos)
 *   children={<OtpForm/>}
 * …and become a perfect visual twin of this screen.
 */
interface AuthShellProps {
  eyebrow: string;
  headline: string;
  subhead: string;
  showcase?: ReactNode;
  /** A static string OR a React node. If omitted, renders the multilingual
   * <WelcomeCycle/> that crossfades across 12 Indian languages. The OTP
   * screen passes a static string like "Enter your code." */
  formTitle?: ReactNode;
  children: ReactNode;
}

/** Tracks the `lg` (1024px) breakpoint so the form (`children`) mounts in exactly ONE of the two
 * responsive layouts below. Previously `children` was rendered in both the desktop and mobile
 * blocks (toggled purely via Tailwind `hidden`/`lg:hidden` classes) — both copies stayed mounted
 * simultaneously, so refs inside children (e.g. the OTP screen's per-digit input refs) bound to
 * whichever copy committed last, breaking focus-advance on whichever viewport didn't "win". */
function useIsDesktop(): boolean {
  const query = "(min-width: 1024px)";
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

export function AuthShell({
  eyebrow,
  headline,
  subhead,
  showcase,
  formTitle,
  children,
}: AuthShellProps) {
  const isDesktop = useIsDesktop();
  // Shared entrance choreography — staggered fade-up with spring overshoot on
  // the form panel itself. Used by both the desktop split panels and the <lg
  // editorial column, so the entrance feels identical across breakpoints.
  const column = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.06 } },
  };
  const item = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: DURATION.entrance, ease: EASE_OUT_SOFT },
    },
  };
  const itemSpring = {
    hidden: { opacity: 0, y: 22, scale: 0.985 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.7, ease: EASE_SPRING },
    },
  };

  return (
    <main
      className="bsure-motion relative min-h-[100dvh] w-full overflow-x-hidden"
      style={{ backgroundColor: "var(--color-canvas)" }}
    >
      {/* ════════════════════════════════════════════════════════════════════
          ≥ lg (1024px+) — DESKTOP SPLIT-SCREEN
          52fr LEFT (brand + showcase) · 48fr RIGHT (form on crisp glass)
          ════════════════════════════════════════════════════════════════════ */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={column}
        className="hidden lg:grid lg:grid-cols-[52fr_48fr] lg:min-h-[100dvh]"
      >
        {/* ─────────────────── LEFT — Brand world ─────────────────── */}
        <section
          aria-label="BeyondSure"
          className="relative overflow-hidden"
          style={{ backgroundColor: "var(--color-canvas)" }}
        >
          {/* Ambient layers — orbs (back) + hex drift + gradient sheen + corner vignette (front) */}
          <div aria-hidden="true" className="absolute inset-0">
            <BgOrbs tone="dark" />
            <HexAmbient tone="dark" />
            <div className="bsure-sheen" />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(120% 80% at 50% 50%, transparent 55%, rgb(0 0 0 / 0.25) 100%)",
              }}
            />
          </div>

          {/* Content stack — top-anchored lockup, centered hero block, bottom
              trust strip. flex-col with mt-auto keeps trust pinned no matter
              how tall the viewport is. */}
          <div className="relative z-10 flex h-full min-h-[100dvh] flex-col px-12 py-12 xl:px-16 xl:py-14">
            <motion.div variants={item}>
              <Lockup variant="white" height={36} />
            </motion.div>

            {/* Hero block — vertically centered between lockup and trust strip.
                Headline + subhead first, then the showcase sits beneath as the
                hero visual. */}
            <div className="flex flex-1 flex-col justify-center gap-7 py-10">
              <motion.h1
                variants={item}
                className={[
                  "bsure-display leading-[0.98]",
                  "text-[2.875rem] xl:text-[3.625rem] 2xl:text-[4rem]",
                  "max-w-[15ch]",
                  "text-[var(--color-paper)]",
                ].join(" ")}
                style={{ fontWeight: 900 }}
              >
                {headline}
              </motion.h1>

              <motion.p
                variants={item}
                className={[
                  "bsure-prose max-w-[42ch]",
                  "text-[16px] xl:text-[17px] leading-[1.55]",
                  "text-[var(--color-paper-72)]",
                ].join(" ")}
              >
                {subhead}
              </motion.p>

              {showcase ? (
                <motion.div
                  variants={itemSpring}
                  className="mt-3 max-w-[480px]"
                >
                  {showcase}
                </motion.div>
              ) : null}
            </div>

            {/* Trust strip — one line, pinned near bottom via mt-auto on its
                ancestor (the flex-1 hero block pushes this down). */}
            <motion.div
              variants={item}
              className={[
                "bsure-prose flex flex-wrap items-center gap-x-4 gap-y-2",
                "text-[12px] tracking-[0.02em]",
                "text-[var(--color-paper-60)]",
              ].join(" ")}
            >
              <span className="flex items-center gap-2">
                <Dot /> 1 Lakh+ Indian families
              </span>
              <Sep tone="paper" />
              <span className="flex items-center gap-2">
                <Dot /> IRDAI-licensed
              </span>
              <Sep tone="paper" />
              <span>English · हिन्दी · 10+ languages</span>
            </motion.div>
          </div>
        </section>

        {/* ─────────────────── RIGHT — Crisp glass form ─────────────────── */}
        <section
          aria-label="Sign in"
          className={[
            "relative flex items-center justify-center",
            "bsure-glass-crisp",
            "px-10 py-12 xl:px-16 xl:py-16",
          ].join(" ")}
        >
          {/* Form column — capped so it never sprawls on 2K/4K monitors. */}
          <motion.div
            variants={itemSpring}
            className="w-full max-w-[440px] flex flex-col gap-7"
          >
            <motion.div
              variants={item}
              className={[
                "bsure-caps flex items-center gap-2",
                "text-[11px] leading-none text-[var(--color-ink-60)]",
              ].join(" ")}
            >
              <span
                aria-hidden="true"
                className="inline-block h-[6px] w-[6px] rounded-full"
                style={{ backgroundColor: "var(--color-brand-vivid)" }}
              />
              {eyebrow}
            </motion.div>

            <motion.div
              variants={item}
              className={[
                // v3.4: the form heading is either a custom formTitle node
                // (e.g. "Enter your code." on the OTP screen) OR the
                // multilingual WelcomeCycle that crossfades 12 languages.
                "leading-[1.05] text-[var(--color-ink)]",
                "text-[2.25rem] xl:text-[2.625rem]",
                "min-h-[2.5em]",
              ].join(" ")}
            >
              {formTitle === undefined ? (
                <WelcomeCycle />
              ) : (
                <h2
                  className="bsure-display leading-[1.05]"
                  style={{ fontWeight: 900 }}
                >
                  {formTitle}
                </h2>
              )}
            </motion.div>

            <motion.div variants={item}>{isDesktop ? children : null}</motion.div>

            <motion.p
              variants={item}
              className={[
                "bsure-prose text-[12.5px] leading-[1.5]",
                "text-[var(--color-ink-40)]",
              ].join(" ")}
            >
              Protected by industry-standard encryption. By continuing you
              agree to BeyondSure's{" "}
              <a
                href="#terms"
                className="text-[var(--color-ink-60)] underline underline-offset-2 hover:text-[var(--color-ink)] transition-colors"
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                href="#privacy"
                className="text-[var(--color-ink-60)] underline underline-offset-2 hover:text-[var(--color-ink)] transition-colors"
              >
                Privacy
              </a>
              .
            </motion.p>
          </motion.div>
        </section>
      </motion.div>

      {/* ════════════════════════════════════════════════════════════════════
          < lg (≤1023px) — STACKED EDITORIAL COLUMN
          Same composition the user accepted on phone + tablet, minus the two
          floating sticker pills. Showcase slot is now visible on tablet too.
          ════════════════════════════════════════════════════════════════════ */}
      <div className="lg:hidden">
        {/* Ambient — tablet: forest-green top fading to mint + orbs */}
        <div className="hidden md:block absolute inset-0 lg:hidden">
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, var(--color-canvas) 0%, var(--color-canvas) 28%, var(--color-mint) 52%, var(--color-mint) 100%)",
            }}
          />
          {/* Orbs in the top forest-green region only */}
          <div className="absolute inset-x-0 top-0 h-[55%]">
            <BgOrbs tone="dark" intensity={0.9} />
          </div>
          <div className="absolute inset-x-0 bottom-0 top-[40%]">
            <HexAmbient tone="light" intensity={0.85} />
          </div>
        </div>

        {/* Ambient — mobile: mint canvas + orbs + light hexagons. The orbs
            do scroll parallax which proves the bg is alive (not a static
            image) per the v5 brief. */}
        <div className="md:hidden absolute inset-0">
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{ backgroundColor: "var(--color-mint)" }}
          />
          <BgOrbs tone="light" intensity={0.85} />
          <HexAmbient tone="light" intensity={0.75} />
        </div>

        {/* Editorial column */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={column}
          className={[
            "relative z-10 mx-auto flex min-h-[100dvh] flex-col",
            "max-w-[600px] px-5 sm:px-6 md:px-8",
            "pt-7 sm:pt-10 md:pt-14",
            "pb-6 sm:pb-8 md:pb-10",
          ].join(" ")}
        >
          <motion.div
            variants={item}
            className="flex items-center justify-center md:justify-start"
          >
            <span className="md:hidden">
              <Lockup variant="full" height={36} />
            </span>
            <span className="hidden md:inline">
              <Lockup variant="white" height={36} />
            </span>
          </motion.div>

          <motion.div
            variants={item}
            className={[
              "mt-10 md:mt-14 flex items-center gap-2",
              "bsure-caps text-[11px] leading-none",
              "text-[var(--color-ink-60)] md:text-[var(--color-paper-72)]",
            ].join(" ")}
          >
            <span
              aria-hidden="true"
              className="inline-block h-[6px] w-[6px] rounded-full"
              style={{ backgroundColor: "var(--color-brand-vivid)" }}
            />
            {eyebrow}
          </motion.div>

          <motion.h1
            variants={item}
            className={[
              "bsure-display mt-4 leading-[0.98]",
              "text-[2.125rem] sm:text-[2.625rem] md:text-[3.125rem]",
              "text-[var(--color-ink)] md:text-[var(--color-paper)]",
            ].join(" ")}
            style={{ fontWeight: 900 }}
          >
            {headline}
          </motion.h1>

          <motion.p
            variants={item}
            className={[
              "bsure-prose mt-4 md:mt-5 max-w-[520px]",
              "text-[15px] md:text-[16px] leading-[1.55]",
              "text-[var(--color-ink-60)] md:text-[var(--color-paper-72)]",
            ].join(" ")}
          >
            {subhead}
          </motion.p>

          {/* Showcase — phone + tablet (was phone-only in v3.1). */}
          {showcase ? (
            <motion.div variants={item} className="mt-8 md:mt-10">
              {showcase}
            </motion.div>
          ) : null}

          <motion.section
            variants={itemSpring}
            aria-label="Sign in"
            className={[
              "mt-8 md:mt-10",
              "rounded-[var(--radius-2xl)]",
              "bsure-glass",
              "px-6 py-8 sm:px-8 sm:py-9 md:px-10 md:py-10",
            ].join(" ")}
          >
            {!isDesktop ? children : null}
          </motion.section>

          <motion.footer
            variants={item}
            className="mt-8 md:mt-10 flex flex-col items-center gap-3 text-center"
          >
            <div
              className={[
                "bsure-prose",
                "flex flex-wrap items-center justify-center gap-x-5 gap-y-2",
                "text-[11.5px] tracking-[0.04em]",
                "text-[var(--color-ink-60)]",
              ].join(" ")}
            >
              <span className="flex items-center gap-2">
                <Dot /> 1 Lakh+ Indian families
              </span>
              <Sep />
              <span className="flex items-center gap-2">
                <Dot /> IRDAI-licensed advisor
              </span>
              <Sep />
              <span className="flex items-center gap-2">
                <Dot /> English · हिन्दी · 10+ languages
              </span>
            </div>

            <div
              className={[
                "bsure-caps flex items-center gap-3 text-[10.5px] leading-none",
                "text-[var(--color-ink-40)]",
              ].join(" ")}
            >
              <a
                href="#privacy"
                className="hover:text-[var(--color-ink)] transition-colors"
              >
                Privacy
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="#terms"
                className="hover:text-[var(--color-ink)] transition-colors"
              >
                Terms
              </a>
            </div>
          </motion.footer>
        </motion.div>
      </div>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────
function Dot() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-[5px] w-[5px] rounded-full"
      style={{ backgroundColor: "var(--color-brand-vivid)" }}
    />
  );
}

function Sep({ tone = "ink" }: { tone?: "ink" | "paper" }) {
  return (
    <span
      aria-hidden="true"
      className={[
        "hidden sm:inline-block h-3 w-px",
        tone === "paper"
          ? "bg-[var(--color-paper-24)]"
          : "bg-[var(--color-ink-16)]",
      ].join(" ")}
    />
  );
}

export default AuthShell;
